const SESSION_KEY = 'cora_session_v2'

interface Session {
  token: string
  email: string
  role: string
  expires_at: string
}

export function decodeJwtClaims(token: string): Record<string, unknown> | null {
  try {
    const payload = token.split('.')[1]
    if (!payload) return null
    return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')))
  } catch {
    return null
  }
}

export function getSession(): Session | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY)
    if (!raw) return null
    const session: Session = JSON.parse(raw)
    if (new Date(session.expires_at) < new Date()) {
      localStorage.removeItem(SESSION_KEY)
      return null
    }
    return session
  } catch {
    return null
  }
}

export function getToken(): string {
  const envToken = import.meta.env.VITE_MCP_AUTH_TOKEN
  if (envToken) return envToken

  const session = getSession()
  if (session?.token) return session.token

  return ''
}

export function isAuthenticated(): boolean {
  return getToken().length > 0
}

export function saveToken(token: string) {
  const claims = decodeJwtClaims(token)
  const email = typeof claims?.email === 'string' ? claims.email : 'ops@bakersguild.co.uk'
  const role = typeof claims?.tenant_role === 'string' ? claims.tenant_role : 'member'
  const exp = typeof claims?.exp === 'number' ? claims.exp : 0
  const expires_at = exp > 0
    ? new Date(exp * 1000).toISOString()
    : new Date(Date.now() + 8 * 60 * 60 * 1000).toISOString()
  const session: Session = { token, email, role, expires_at }
  localStorage.setItem(SESSION_KEY, JSON.stringify(session))
  window.dispatchEvent(new Event('ops-auth-change'))
}

export function clearSession() {
  // Revoke browser-connect sessions from either accepted Dashboard login:
  // Core JWT or newer Gateway opaque MFA session. Only the saved user session
  // is sent, never any optional static/service token from getToken().
  const active = getSession()
  if (active?.token) {
    void fetch('/api/auth/browser-connect/handoff/logout', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${active.token}`,
        'Content-Type': 'application/json',
      },
      body: '{}',
      cache: 'no-store',
      keepalive: true,
    }).catch(() => {})
  }
  localStorage.removeItem(SESSION_KEY)
  window.dispatchEvent(new Event('ops-auth-change'))
}

export async function refreshToken(): Promise<string | null> {
  const session = getSession()
  if (!session?.token) return null

  try {
    const res = await fetch(`/api/oauth/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ grant_type: 'refresh_token', token: session.token }),
    })
    if (!res.ok) return null
    const data = await res.json()
    if (data.access_token) {
      saveToken(data.access_token)
      return data.access_token
    }
    return null
  } catch {
    return null
  }
}
